import { useState, useEffect, useCallback, Dispatch, SetStateAction } from 'react';
// @mui
import Collapse from '@mui/material/Collapse';
// routes
import { usePathname } from 'src/routes/hooks';
import { useActiveLink } from 'src/routes/hooks/use-active-link';
//
import { NavListProps, NavConfigProps } from '../types';
import NavItem from './nav-item';

// ----------------------------------------------------------------------

type NavListRootProps = {
  data: NavListProps;
  depth: number;
  hasChild: boolean;
  config: NavConfigProps;
};

type setOpenType = Dispatch<SetStateAction<boolean>>;
type toggleObserverType = {
  setOpen: setOpenType,
  depth: number,
}
let toToggle: toggleObserverType[] = [];
function registerOnToggle(setOpen: setOpenType, depth: number) {
  toToggle.push({ setOpen, depth });
}
function deregisterOnToggle(setOpen: setOpenType) {
  toToggle = toToggle.filter(a => a.setOpen !== setOpen);
}
function triggerOnToggle(setOpen: setOpenType, depth: number) {
  setOpen((prev) => !prev);
  toToggle.forEach(s => {
    if (s.setOpen !== setOpen && s.depth === depth) {
      s.setOpen(false)
    }
  })
}

export default function NavList({ data, depth, hasChild, config }: NavListRootProps) {
  const active = useActiveLink(data.path, hasChild);

  const externalLink = data.path?.includes('http');

  const [open, setOpen] = useState(active);

  useEffect(() => {
    if (!active) {
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    registerOnToggle(setOpen, depth);
    return () => deregisterOnToggle(setOpen);
  }, [depth]);

  const handleToggle = useCallback(() => {
    triggerOnToggle(setOpen, depth);
  }, [depth]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <>
      <NavItem
        item={data}
        depth={depth}
        open={open}
        active={active}
        externalLink={externalLink}
        onClick={handleToggle}
        config={config}
      />

      {hasChild && (
        <Collapse in={open} unmountOnExit>
          <NavSubList data={data.children} depth={depth} config={config} />
        </Collapse>
      )}
    </>
  );
}

// ----------------------------------------------------------------------

type NavListSubProps = {
  data: NavListProps[];
  depth: number;
  config: NavConfigProps;
};

function NavSubList({ data, depth, config }: NavListSubProps) {
  return (
    <>
      {data.map((list) => (
        <NavList
          key={list.title + list.path}
          data={list}
          depth={depth + 1}
          hasChild={!!list.children}
          config={config}
        />
      ))}
    </>
  );
}
